import { Button } from '@monorepo/shared/componentsV2/Button';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { useDataset } from '@monorepo/shared/hooks/workflows/useDataset';
import { findStepInputDataset } from '@monorepo/workflowDataSetup/src/components/EditWorkflowStepsPage/stepUtils';
import AddIcon from '@svg/add.svg';
import { JoinStepConditionRequest, WorkflowResponse } from 'mapistry-shared';
import React, { useCallback, useMemo, useRef } from 'react';
import { useField } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import styled from 'styled-components';
import { DateIntervalCondition } from './DateIntervalCondition';
import {
  areAllRegularConditionsOptional,
  doesNeedDateIntervalCondition,
  getTimeSeriesJoinType,
} from './helpers';
import { StepCondition } from './StepCondition';
import {
  StyledConditionsContainer,
  StyledDescription,
  StyledH3,
} from './styled';

export const StyledDateIntervalCondition = styled(DateIntervalCondition)`
  margin-bottom: 2rem;
`;

interface JoinConditionsModalPageProps {
  workflow: WorkflowResponse;
  stepId?: string;
}

function JoinConditionsModalPageContent({
  stepId,
  workflow,
}: JoinConditionsModalPageProps) {
  const {
    input: { value: selectedJoinToDatasetId },
  } = useField<string>('joinToDatasetId');

  const { dataset: joinToDataset } = useDataset({
    organizationId: workflow.organizationId,
    datasetId: selectedJoinToDatasetId,
  });
  const stepInputDataset = useMemo(
    () => findStepInputDataset(workflow, stepId),
    [stepId, workflow],
  );
  const timeSeriesJoinType = useMemo(
    () =>
      joinToDataset && getTimeSeriesJoinType(stepInputDataset, joinToDataset),
    [stepInputDataset, joinToDataset],
  );
  const doesNeedDateCondition = useMemo(
    () =>
      !!timeSeriesJoinType && doesNeedDateIntervalCondition(timeSeriesJoinType),
    [timeSeriesJoinType],
  );
  const areAllConditionsOptional = useMemo(
    () =>
      !!timeSeriesJoinType &&
      areAllRegularConditionsOptional(timeSeriesJoinType),
    [timeSeriesJoinType],
  );

  /**
   * move focus to the add button if it would get lost otherwise
   * (e.g. if we're deleting the last condition in the list, or there are only 2 left and we delete one)
   */
  const addConditionButtonRef = useRef<HTMLButtonElement>(null);
  const preventLosingFocus = useCallback((conditions, idx) => {
    if (conditions.length === 2 || idx + 1 === conditions.length) {
      addConditionButtonRef.current?.focus();
    }
  }, []);

  return (
    <>
      <StyledH3>Match fields</StyledH3>
      <StyledDescription>
        Choose which fields you’d like to match from each of your data sources.
      </StyledDescription>

      {doesNeedDateCondition && joinToDataset && (
        <StyledDateIntervalCondition
          leftDataset={stepInputDataset}
          rightDataset={joinToDataset}
        />
      )}

      <FieldArray<Partial<JoinStepConditionRequest>> name="conditions">
        {({ fields: conditions }) => (
          <div>
            {!!conditions.length && conditions.length > 0 && (
              <StyledConditionsContainer>
                {conditions.map((conditionName, idx) => (
                  <StepCondition
                    canDelete={
                      areAllConditionsOptional || conditions.value.length > 1
                    }
                    formFieldName={conditionName}
                    key={conditionName}
                    leftDataset={stepInputDataset}
                    rightDataset={joinToDataset}
                    shouldLabelShow={idx === 0}
                    onDelete={() => {
                      preventLosingFocus(conditions, idx);
                      conditions.remove(idx);
                    }}
                  />
                ))}
              </StyledConditionsContainer>
            )}

            <Button
              ref={addConditionButtonRef}
              color="primary"
              onClick={() => {
                conditions.push({
                  leftPropertyId: undefined,
                  rightPropertyId: undefined,
                });
              }}
            >
              <AddIcon />
              Add another
            </Button>
          </div>
        )}
      </FieldArray>
    </>
  );
}

export function JoinConditionsModalPage(props: JoinConditionsModalPageProps) {
  return (
    <ErrorBoundary>
      <JoinConditionsModalPageContent {...props} />
    </ErrorBoundary>
  );
}
